import React, { useEffect, useState } from 'react';
import { 
  TextField, 
  Button, 
  Grid, 
  Typography, 
  MenuItem, 
  Container, 
  CircularProgress, 
  Tabs, 
  Tab, 
  Box,
  Card,
  CardContent,
  Fade,
  Chip,
  alpha
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import CalculateIcon from '@mui/icons-material/Calculate';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Navbar from '../../globals/components/Navbar';
import { apiClient } from '../../globals/utils/api';
import { calculatePremium } from '../../globals/utils/commons';

const categories = [
  { category: "Probate" },
  { category: "Guardianship", sub_categories: ["Guardian - Incapacitated", "Guardian - Minor", "Guardian - Advocate", "Conservator"] },
  { category: "Trustee" },
  { category: "VA" },
  { category: "Blanket" }
];

const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      isNumericString
      prefix="$"
      decimalScale={0}
    />
  );
});

const formatCurrency = (amount) => {
  return '$' + Math.round(amount).toLocaleString('en-US', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  });
};

const getBondFromGross = (gross) => {
  if (gross <= 75000) return 18000;
  if (gross <= 100000) return 25000;
  if (gross <= 175000) return 35000;
  if (gross <= 250000) return 50000;
  if (gross <= 500000) return 75000;
  return 100000 + Math.ceil((gross - 500000) / 500000) * 100000;
};

const THEME = {
  blue: '#2563eb',
  blueRgba: (opacity) => `rgba(37, 99, 235, ${opacity})`,
  cardBg: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f8f9fa 100%)',
  cardBorder: '1px solid rgba(37, 99, 235, 0.2)',
  titleGradient: 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 50%, #111827 100%)',
  buttonBg: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
  buttonHover: 'linear-gradient(135deg, #1d4ed8 0%, #1e3a8a 100%)',
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: alpha('#2563eb', 0.02),
    '& fieldset': { borderColor: THEME.blueRgba(0.2) },
    '&:hover fieldset': { borderColor: THEME.blueRgba(0.4) },
    '&.Mui-focused fieldset': {
      borderColor: THEME.blue,
      borderWidth: 2,
    },
  },
};

const PremiumCalculator = () => {
  const { handleSubmit, control, watch, setValue } = useForm();
  const [premium, setPremium] = useState(null);
  const [states, setStates] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [loadingCalculation, setLoadingCalculation] = useState(false);
  const [tab, setTab] = useState(0);
  const { handleSubmit: handleSubmitPinellas, control: controlPinellas } = useForm();
  const [pinellasBond, setPinellasBond] = useState(null);
  const [pinellasPremium, setPinellasPremium] = useState(null);
  const [homesteadPercentage, setHomesteadPercentage] = useState(0);

  const bondType = watch("bondType");
  const bondSubCategory = watch("bondSubCategory");

  const fetchStates = async () => {
    setLoadingDropdowns(true);
    try {
      const statesRes = await apiClient(`/states/0/`, "GET");
      setStates(Array.isArray(statesRes) ? statesRes : statesRes || []);
    } catch (error) {
      console.error("Error fetching states:", error);
    } finally {
      setLoadingDropdowns(false);
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    if (bondType === "Blanket") {
      setValue("bondSubCategory", "Blanket");
      setValue("bondAmount", "");
      setValue("dateOfBirth", "");
    } else {
      setValue("bondTerm", "");
    }
  }, [bondType, setValue]);

  useEffect(() => {
    const fetchHomesteadPercentage = async () => {
      try {
        const response = await apiClient('/pinellas-homestead/', 'GET');
        setHomesteadPercentage(response.homestead_percentage || 0);
      } catch (error) {
        console.error('Error fetching homestead percentage:', error);
        setHomesteadPercentage(0);
      }
    };
    fetchHomesteadPercentage();
  }, []);

  const onSubmit = (data) => {
    const { bondSubCategory, bondAmount, dateOfBirth, bondTerm } = data;
    setLoadingCalculation(true);
    try {
      const premium = calculatePremium(bondAmount, dateOfBirth, bondSubCategory, bondTerm);
      setPremium(formatCurrency(premium));
    } catch (error) {
      console.error("Error calculating premium:", error);
      setPremium(null);
    } finally {
      setLoadingCalculation(false);
    }
  };

  const onSubmitPinellas = async (data) => {
    const { homestead, other } = data;
    
    const homesteadValue = parseInt(homestead.replace(/[^0-9]/g, '')) || 0;
    const otherValue = parseInt(other.replace(/[^0-9]/g, '')) || 0;
    
    const homesteadPortion = homesteadValue * (homesteadPercentage / 100);
    const gross = homesteadPortion + otherValue;
    const bondAmount = getBondFromGross(gross);
    const premium = calculatePremium(bondAmount);
    
    setPinellasBond(formatCurrency(bondAmount));
    setPinellasPremium(formatCurrency(premium));
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="md" sx={{ py: 4, mt: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 700,
              color: '#000000',
              mb: 1
            }}
          >
            Premium Calculator
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            Calculate your bond premium instantly with our advanced calculator
          </Typography>
        </Box>

        <Card 
          elevation={0}
          sx={{ 
            borderRadius: 4,
            background: THEME.cardBg,
            border: THEME.cardBorder,
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(37, 99, 235, 0.15)',
            },
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: THEME.blueRgba(0.12) }}>
            <Tabs 
              value={tab} 
              onChange={(e, newVal) => setTab(newVal)} 
              centered
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  py: 3,
                  minHeight: 72,
                  '&.Mui-selected': {
                    color: THEME.blue,
                  },
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  background: THEME.buttonBg,
                },
              }}
            >
              <Tab 
                icon={<CalculateIcon />}
                iconPosition="start"
                label="Premium Calculator" 
                sx={{ gap: 1 }}
              />
              <Tab 
                icon={<TrendingUpIcon />}
                iconPosition="start"
                label="Pinellas County Calculator" 
                sx={{ gap: 1 }}
              />
            </Tabs>
          </Box>

          <CardContent sx={{ p: 4 }}>
            {/* Premium Calculator Tab */}
            <Fade in={tab === 0} timeout={300}>
              <Box sx={{ display: tab !== 0 ? 'none' : 'block' }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AttachMoneyIcon sx={{ color: '#000000' }} />
                    <Typography variant="h5" fontWeight={600} sx={{ color: '#000000' }}>
                      Premium Calculator
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary">
                    Enter your bond details to calculate the premium
                  </Typography>
                </Box>

                {loadingDropdowns ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CircularProgress size={40} thickness={4} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Loading calculator...
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={3}>
                      {/* State Dropdown */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                          State *
                        </Typography>
                        <Controller
                          name="state"
                          control={control}
                          defaultValue=""
                          render={({ field }) => (
                            <TextField
                              {...field}
                              select
                              fullWidth
                              variant="outlined"
                              required
                              placeholder="Select your state"
                              sx={inputSx}
                            >
                              {states.map((option) => (
                                <MenuItem key={option.bond_state_id} value={option.bond_state_id}>
                                  {option.state_name}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      </Grid>

                      {/* Bond Type Dropdown */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                          Bond Type *
                        </Typography>
                        <Controller
                          name="bondType"
                          control={control}
                          defaultValue=""
                          render={({ field }) => (
                            <TextField
                              {...field}
                              select
                              fullWidth
                              variant="outlined"
                              required
                              placeholder="Select bond type"
                              sx={inputSx}
                            >
                              {categories.map((option, index) => (
                                <MenuItem key={index} value={option.category}>
                                  {option.category}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      </Grid>

                      {/* Bond SubCategory Dropdown for Guardianship */}
                      {bondType === "Guardianship" && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                            Bond Sub Category *
                          </Typography>
                          <Controller
                            name="bondSubCategory"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                select
                                fullWidth
                                variant="outlined"
                                required
                                placeholder="Select sub category"
                                sx={inputSx}
                              >
                                {categories.find(cat => cat.category === "Guardianship").sub_categories.map((subCat, index) => (
                                  <MenuItem key={index} value={subCat}>
                                    {subCat}
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                          />
                        </Grid>
                      )}

                      {/* Bond Term Dropdown for Blanket */}
                      {bondType === "Blanket" && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                            Bond Term *
                          </Typography>
                          <Controller
                            name="bondTerm"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                select
                                fullWidth
                                variant="outlined"
                                required
                                placeholder="Select term"
                                sx={inputSx}
                              >
                                <MenuItem value={1}>1 Year</MenuItem>
                                <MenuItem value={2}>2 Years</MenuItem>
                                <MenuItem value={3}>3 Years</MenuItem>
                              </TextField>
                            )}
                          />
                        </Grid>
                      )}

                      {/* Bond Amount Field */}
                      {bondType !== "Blanket" && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                            Bond Amount *
                          </Typography>
                          <Controller
                            name="bondAmount"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                variant="outlined"
                                required
                                placeholder="Enter bond amount"
                                InputProps={{
                                  inputComponent: NumberFormatCustom,
                                  inputProps: {
                                    min: 0
                                  }
                                }}
                                sx={inputSx}
                              />
                            )}
                          />
                        </Grid>
                      )}

                      {/* Date of Birth Field for Guardian - Minor */}
                      {bondSubCategory === "Guardian - Minor" && bondType === "Guardianship" && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                            Date of Birth *
                          </Typography>
                          <Controller
                            name="dateOfBirth"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                type="date"
                                fullWidth
                                variant="outlined"
                                required
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                sx={inputSx}
                              />
                            )}
                          />
                        </Grid>
                      )}

                      {/* Calculate Button */}
                      <Grid item xs={12}>
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                          <Button 
                            type="submit" 
                            variant="contained" 
                            size="large"
                            disabled={loadingCalculation}
                            startIcon={loadingCalculation ? <CircularProgress size={16} color="inherit" /> : <CalculateIcon />}
                            sx={{
                              borderRadius: 3,
                              px: 4,
                              py: 1.5,
                              fontSize: '1rem',
                              fontWeight: 600,
                              background: THEME.buttonBg,
                              boxShadow: `0 4px 20px ${THEME.blueRgba(0.35)}`,
                              '&:hover': {
                                background: THEME.buttonHover,
                                boxShadow: `0 6px 25px ${THEME.blueRgba(0.4)}`,
                                transform: 'translateY(-2px)',
                              },
                              '&:disabled': {
                                background: alpha(THEME.blue, 0.3),
                              },
                              transition: 'all 0.3s ease',
                            }}
                          >
                            {loadingCalculation ? "Calculating..." : "Calculate Premium"}
                          </Button>
                        </Box>
                      </Grid>

                      {/* Display Calculated Premium */}
                      {premium && (
                        <Grid item xs={12}>
                          <Fade in={!!premium} timeout={500}>
                            <Card 
                              sx={{ 
                                background: THEME.cardBg,
                                border: '2px solid',
                                borderColor: THEME.blueRgba(0.3),
                                borderRadius: 3,
                                mt: 2,
                                boxShadow: `0 2px 8px ${THEME.blueRgba(0.1)}`,
                              }}
                            >
                              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                  Estimated Premium
                                </Typography>
                                <Typography 
                                  variant="h3" 
                                  sx={{ 
                                    fontWeight: 700,
                                    background: THEME.titleGradient,
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    mb: 1
                                  }}
                                >
                                  {premium}
                                </Typography>
                                <Chip 
                                  label="Instant Calculation" 
                                  sx={{ borderColor: THEME.blueRgba(0.5), color: THEME.blue }}
                                  variant="outlined"
                                  size="small"
                                />
                              </CardContent>
                            </Card>
                          </Fade>
                        </Grid>
                      )}
                    </Grid>
                  </form>
                )}
              </Box>
            </Fade>

            {/* Pinellas County Calculator Tab */}
            <Fade in={tab === 1} timeout={300}>
              <Box sx={{ display: tab !== 1 ? 'none' : 'block' }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrendingUpIcon sx={{ color: '#000000' }} />
                    <Typography variant="h5" fontWeight={600} sx={{ color: '#000000' }}>
                      Pinellas County Calculator
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Specialized calculator for Pinellas County properties
                  </Typography>
                  <Chip 
                    label={`Current Homestead Percentage: ${homesteadPercentage}%`}
                    sx={{ borderColor: THEME.blueRgba(0.5), color: THEME.blue }}
                    variant="outlined"
                  />
                </Box>

                <form onSubmit={handleSubmitPinellas(onSubmitPinellas)}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                        Homestead Property Value *
                      </Typography>
                      <Controller
                        name="homestead"
                        control={controlPinellas}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            variant="outlined"
                            required
                            placeholder="Enter homestead value"
                            InputProps={{
                              inputComponent: NumberFormatCustom,
                              inputProps: {
                                min: 0
                              }
                            }}
                            sx={inputSx}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                        Other Property Value *
                      </Typography>
                      <Controller
                        name="other"
                        control={controlPinellas}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            variant="outlined"
                            required
                            placeholder="Enter other property value"
                            InputProps={{
                              inputComponent: NumberFormatCustom,
                              inputProps: {
                                min: 0
                              }
                            }}
                            sx={inputSx}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Button 
                          type="submit" 
                          variant="contained" 
                          size="large"
                          startIcon={<TrendingUpIcon />}
                          sx={{
                            borderRadius: 3,
                            px: 4,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            background: THEME.buttonBg,
                            boxShadow: `0 4px 20px ${THEME.blueRgba(0.35)}`,
                            '&:hover': {
                              background: THEME.buttonHover,
                              boxShadow: `0 6px 25px ${THEME.blueRgba(0.4)}`,
                              transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          Calculate Bond & Premium
                        </Button>
                      </Box>
                    </Grid>

                    {pinellasBond && pinellasPremium && (
                      <Grid item xs={12}>
                        <Fade in={!!(pinellasBond && pinellasPremium)} timeout={500}>
                          <Card 
                            sx={{ 
                              background: THEME.cardBg,
                              border: '2px solid',
                              borderColor: THEME.blueRgba(0.3),
                              borderRadius: 3,
                              mt: 2,
                              boxShadow: `0 2px 8px ${THEME.blueRgba(0.1)}`,
                            }}
                          >
                            <CardContent sx={{ textAlign: 'center', py: 3 }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                    Estimated Bond
                                  </Typography>
                                  <Typography 
                                    variant="h4" 
                                    sx={{ 
                                      fontWeight: 700,
                                      color: THEME.blue,
                                    }}
                                  >
                                    {pinellasBond}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                    Estimated Premium
                                  </Typography>
                                  <Typography 
                                    variant="h4" 
                                    sx={{ 
                                      fontWeight: 700,
                                      color: THEME.blue,
                                    }}
                                  >
                                    {pinellasPremium}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </Fade>
                      </Grid>
                    )}
                  </Grid>
                </form>
              </Box>
            </Fade>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

export default PremiumCalculator;